import { resolveLang } from 'common/handleLocale';

// APIS always reject with error string

const ajax = ({ url, data, ...props }, alertOnError = false) =>
  new Promise(async (resolve, reject) => {
    try {
      const result = await $.ajax({
        url: `${window.AS.domains.api}${url}`,
        data: data ? JSON.stringify(data) : undefined,
        type: 'POST',
        contentType: 'application/json',
        ...props,
      });
      resolve(result);
    } catch (error) {
      if (alertOnError) {
        alert(resolveLang('エラーが発生しました。'));
      }
      reject(error);
    }
  });

const routeAjax = ({ url, data, ...props }) =>
  $.ajax({
    url: `${window.AS.domains.route}${url}`,
    data: data ? JSON.stringify(data) : undefined,
    type: 'POST',
    contentType: 'application/json',
    ...props,
  });

const throwAlert = (message) => {
  alert(message);
  throw new Error(message);
};

/**
 * @type
 * {(device: string, userData: {
    accessToken: string;
    gender: string;
    email: string;
    name: string;
    picture?: {
      data?: {
        url: string;
      };
    };
    id: string;
}) => Promise<any>}
 */
export const loginFacebook = (
  device,
  {
    accessToken,
    gender,
    email,
    name,
    picture: { data: { url } = { url: '' } } = {},
    id,
  }
) => {
  if (!id) {
    return Promise.reject();
  }

  return ajax({
    url: `/LOGIN/FACEBOOK/${id}/${device}`,
    data: {
      account: id,
      password: accessToken,
      sex: gender === 'male' ? '1' : '2',
      mail: email,
      nickname: name,
      icon_url: url,
    },
  });
};

// ACCOUNT ==============================================
export const AccountUpdate = ({ session, set, unset }) =>
  ajax({
    url: '/ACCOUNT/UPDATE',
    data: {
      session,
      set,
      unset,
    },
  });

// AUTH ===============================================
export const AuthSetDeviceToken = ({ session, token, setDevice }) =>
  ajax({
    url: '/AUTH/SET_DEVICE_TOKEN',
    data: {
      session,
      token,
      set_device: setDevice,
    },
  });

// MAP ==============================================
/**
 * @type
 * {(param: MAP_CREATE) => Promise<any>}
 */
export const MapCreate = (param) => {
  const { session, title } = param;
  if (!session) {
    throwAlert(resolveLang('セッションが設定されてないです'));
  }

  if (!title) {
    throwAlert(resolveLang('マップのタイトルを入力してください'));
  }

  return ajax(
    {
      url: '/MAP/CREATE',
      data: param, // _id は指定できない
    },
    true
  );
};

/**
 * @type
 * {(param: MAP_UPDATE) => Promise<any>}
 */
export const MapUpdate = (param) => {
  const { session, _id } = param;
  if (!session) {
    throwAlert(resolveLang('セッションが設定されてないです'));
  } else if (!_id) {
    throwAlert(resolveLang('マップのIDを指定してください'));
  }

  return ajax(
    {
      url: '/MAP/UPDATE',
      data: param,
    },
    true
  );
};

/**
 * @typedef {{
      skip?: number;
      limit?: number;
      categories?: string[];
      not_categories?: string[];
      prefectures?: string[];
      keyword?: string;
      count?: number;
      random_order?: boolean;
      force_show_account_ids?: string[];
      ids?: string[];
      not_special_contents?: string[];
      locale?: string;
     }} sharedPostData
 */

/**
 * @type {
    (data: sharedPostData) => Promise<{maps: object[], count?: number}>
 * }
 */
export const MapSharedNewest = (data) =>
  ajax({
    url: '/MAP/SHARED/NEWEST',
    data:
      window.AS.mapSpecialContents && !data.ids
        ? { ...data, special_contents: window.AS.mapSpecialContents }
        : data,
  });

/**
 * @type {
    (data: sharedPostData) => Promise<{maps: object[], count?: number}>
 * }
 */
export const MapSharedOfficial = (data) =>
  ajax({
    url: '/MAP/SHARED/OFFICIAL',
    data:
      window.AS.mapSpecialContents && !data.ids
        ? { ...data, special_contents: window.AS.mapSpecialContents }
        : data,
  });

/**
 * @type {
    (data: sharedPostData) => Promise<{maps: object[], count?: number}>
 * }
 */
export const MapSharedPopular = (data) =>
  ajax({
    url: '/MAP/SHARED/POPULAR',
    data:
      window.AS.mapSpecialContents && !data.ids
        ? { ...data, special_contents: window.AS.mapSpecialContents }
        : data,
  });

// SNS ===============================================
export const SnsGood = ({ session, goodFrom, isDelete, locale }) =>
  ajax({
    url: '/SNS/GOOD',
    data: {
      session,
      good_from: goodFrom,
      isDelete,
      locale,
    },
  });

export const SnsFollow = ({ session, followFrom, isDelete, locale }) =>
  ajax({
    url: '/SNS/FOLLOW',
    data: {
      session,
      follow_from: followFrom,
      isDelete,
      locale,
    },
  });

export const SnsCommentCreate = ({ session, commentFrom, text, registration_datetime }) =>
  ajax({
    url: '/SNS/COMMENT/CREATE',
    data: {
      session,
      comment_from: commentFrom,
      text,
      registration_datetime
    },
  });

export const SnsCommentEdit = ({ session, commentId, text }) =>
  ajax({
    url: '/SNS/COMMENT/EDIT',
    data: {
      session,
      comment_id: commentId,
      text,
    },
  });

export const SnsCommentDelete = ({ session, _id }) =>
  ajax({
    url: '/SNS/COMMENT/DELETE',
    data: {
      session,
      _id,
    },
  });

// NOTIFICATION ===============================================
/**
 * @type
 * {(param: {offset?: number, limit?: number, session: string}) => Promise<MplfNotifications>}
 */
export const MPLFNotifications = async ({ offset, limit, session }) => {
  const { result } = await routeAjax({
    url: '/mplfNotifications',
    data: {
      offset,
      limit,
      session,
    },
  });

  return result;
};

export const NotificationDetail = ({
  session,
  locale,
  sender_ids,
  dialy_ids,
  spot_ids,
  map_ids,
}) =>
  ajax({
    url: '/NOTIFICATION/DETAIL',
    data: {
      session,
      locale,
      sender_ids,
      dialy_ids,
      spot_ids,
      map_ids,
    },
  });

export const MPLFBadgeAllClear = async ({ session }) =>
  routeAjax({
    url: '/mplfBadgeAllClear',
    data: {
      session,
    },
  });

export const MPLFRead = async ({ session, notification_id }) =>
  routeAjax({
    url: '/mplfRead',
    data: {
      session,
      notification_id,
    },
  });

export const MPLFReadAll = async ({ session }) =>
  routeAjax({
    url: '/mplfReadAll',
    data: {
      session,
    },
  });

// BUS ROUTE ==============================================
/**
 * @type {(param: {
 *  arriveTo: {
 *    lat: number,
 *    lng: number
 *  };
 *  departFrom: {
 *    lat: number,
 *    lng: number
 *  };
 * }) => Promise<any>}
 */
export const BusRoute = async ({ arriveTo, departFrom }) => {
  const src = `${departFrom.lng} ${departFrom.lat}`;
  const dst = `${arriveTo.lng} ${arriveTo.lat}`;

  const result = await $.ajax({
    url: `${window.AS.domains.route}/${
      !window.hlocale || window.hlocale === 'ja' ? 'BusRoute' : 'BusRouteRoma'
    }`,
    type: 'POST',
    contentType: 'application/x-www-form-urlencoded; charset=utf-8',
    data: {
      src,
      dst,
    },
  });

  return result;
};

// MAP_GROUP ==============================================
/**
 * @type {(param: {
 *  session: string;
 *  map_id: string;
 *  mail: string;
 *  force: boolean;
 *  locale: string;
 * }) => Promise<{
 *  success: boolean;
 *  mail_is_not_registered: boolean;
 *  mapGroup: {
 *    nickname: string;
 *    account_id: string;
 *    mail: string;
 *    icon_url: string;
 *    accepted: '0' | '1';
 *  };
 * }>}
 */
export const MapGroupInviteParticipant = ({
  session,
  map_id,
  mail,
  force,
  locale,
}) =>
  ajax({
    url: '/MAP_GROUP/INVITE_PARTICIPANT',
    data: {
      session,
      map_id,
      mail,
      force,
      locale,
    },
  });

/**
 * @type {(param: {
 *  session: string;
 *  map_id: string;
 *  mail?: string;
 *  status: 'accept' | 'decline' | 'delete';
 *  locale: string;
 * }) => Promise<{
 *  success: boolean;
 * }>}
 */
export const MapGroupUpdateInvitation = ({
  session,
  map_id,
  mail,
  status,
  locale,
}) =>
  ajax({
    url: '/MAP_GROUP/UPDATE_INVITATION',
    data: {
      session,
      map_id,
      mail,
      status,
      locale,
    },
  });

/**
 * @type {(param: {
 *  session: string;
 *  locale: string;
 * }) => Promise<{
 *    _id: string;
 *    map_id: string;
 *    account_id: string;
 *    accepted: '0' | '1';
 *    map: {
 *      title: string;
 *      lang: {};
 *      image_id: string;
 *    };
 *    account: {
 *      nickname: string;
 *      icon_url: string;
 *    };
 * }[]>}
 */
export const MapGroupGetInvitations = ({ session, locale }) =>
  ajax({
    url: '/MAP_GROUP/GET_INVITATIONS',
    data: {
      session,
      locale,
    },
  });

/**
 * @param {{session: string, token: string, latitude: number, longitude: number}} payload
 * @return {Promise<MISSION_FULFILL>}
 */
export const MissionFulfill = (payload) =>
  ajax({
    url: '/MISSION/FULFILL',
    data: payload,
  });

// PHOTO_CONTEST ==============================================
/**
 * @param {{session: string, dialy_id: string, image_id: string, is_visible: boolean}} payload
 */
export const PhotoContestSetVisible = (payload) =>
  ajax({
    url: '/PHOTO_CONTEST/SET_VISIBLE',
    data: payload,
  });
